import { api } from "@/lib/api";
import type { Contact } from "./contacts-api";
import { locale, t } from "@/lib/i18n";

/** Компании и связь с Zoho Books (backend/hub/api/companies.py, zoho.py). Zoho только читаем. */

export type Suggestion = { kind: "company" | "contact"; id: string; name: string; rule: string; why: string };

export type ZohoCustomer = {
  booksId: string;
  name: string;
  companyName: string;
  business: boolean;
  email: string | null;
  phone: string | null;
  trn: string;
  country: string;
  salesperson: string;
  segment: string;
  revenue: number;
  receivable: number;
  invoices: number;
  lastInvoiceAt: string | null;
  persons: { name: string; email: string | null; phone: string | null; mobile: string | null; primary: boolean }[];
  linkStatus: "unlinked" | "suggested" | "linked" | "rejected";
  linkRule: string | null;
  linkWhy: string;
  companyId: string | null;
  contactId: string | null;
  companyTitle?: string | null;
  contactTitle?: string | null;
  suggestions: Suggestion[];
  booksUrl: string;
};

export type Company = {
  id: string;
  name: string;
  segment: string;
  department: string;
  country: string;
  city: string;
  address: string;
  trn: string;
  phone: string | null;
  email: string | null;
  website: string;
  leadSource: string;
  notes: string;
  ownerId: string | null;
  zcrmAccountId: string | null;
  people: number;
  revenue: number;
  receivable: number;
  lastInvoiceAt: string | null;
  contacts?: (Contact & { companyRole: string })[];
  hiddenContacts?: number;
  zoho?: ZohoCustomer[];
  /** карточка клиента по макету: ответственный, продавец Zoho, сводка сделок, число диалогов */
  owner?: { id: string; name: string } | null;
  salesperson?: string;
  deals?: DealStats;
  conversations?: number;
};

/** вероятные дубли компаний (GET /companies/duplicates): группа по названию без правовой формы или по TRN */
export type DuplicateItem = {
  id: string;
  name: string;
  trn: string;
  country: string;
  zcrmAccountId: string | null;
  owner: string | null;
  people: number;
  deals: number;
  books: number;
  revenue: number;
  createdAt: string;
};
export type DuplicateGroup = { key: string; why: "name" | "trn"; keep: string; revenue: number; items: DuplicateItem[] };
export type MergeResult = Company & { merged: { merged: string; mergedName: string; people: number; deals: number; books: number; notes: number; tasks: number }[] };

export type DealStats = { total: number; open: number; openSum: number; won: number; wonSum: number; lost: number; currency: string };

/** событие единой ленты клиента (GET /companies/{id}/timeline) */
export type TimelineItem = { at: string; kind: "message" | "invoice" | "order" | "payment" | "creditnote" | "deal" | "note" | "task" } & Record<string, any>;
export type HistoryItem = { at: string; actor: string | null; action: string; entity: string; entityId: string; details: Record<string, unknown> };

/** Счётчики переключателя «Мои / Без ответственного / Все» (с учётом строки поиска) */
export type OwnerCounts = { me: number; none: number; all: number };

export type CompanySort = "name" | "country" | "segment" | "people" | "revenue" | "receivable" | "last_invoice" | "owner";
/** суммы, люди и даты по умолчанию по убыванию, текст — по возрастанию (как на сервере) */
export const SORT_DESC_DEFAULT: ReadonlySet<CompanySort> = new Set(["people", "revenue", "receivable", "last_invoice"]);

export type CompanyInput = Partial<Pick<Company, "name" | "segment" | "country" | "city" | "address" | "trn" | "phone" | "email" | "website" | "notes" | "ownerId">>;

export type SalespersonRow = {
  name: string;
  email: string;
  active: boolean;
  deals: number;
  dialogs: number;
  unassigned: number;
  userId: string | null;
  user: string | null;
  userEmail: string | null;
};

export type SalespersonsStatus = {
  items: SalespersonRow[];
  users: { id: string; name: string; email: string; role: string }[];
  configured: boolean;
  running: boolean;
  startedAt: string | null;
  okAt: string | null;
  error: string | null;
  stats: { resolved?: number; assigned?: number; deals?: number; scope?: string } | null;
  withoutSalesperson: number;
};

export type ZohoStatus = {
  configured: boolean;
  everyMinutes: number;
  running: boolean;
  sync: { kind: string; startedAt: string | null; finishedAt: string | null; okAt: string | null; stats: Record<string, unknown>; error: string | null }[];
  buyers: Record<ZohoCustomer["linkStatus"], number>;
  revenue: Record<ZohoCustomer["linkStatus"], number>;
  customers: number;
  companies: number;
  wazzup: {
    channelId: string | null;
    running: boolean;
    startedAt: string | null;
    finishedAt: string | null;
    okAt: string | null;
    error: string | null;
    lastModified: string | null;
    conversations: number;
    messages: number;
    contacts: number;
    run: { messages: number; conversations: number; skipped: number; pages: number } | null;
  };
};

export const PERSON_ROLES: Record<string, string> = { buyer: t("Закупщик"), owner: t("Владелец"), accounting: t("Бухгалтерия"), manager: t("Руководитель"), other: t("Другое") };

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
};

export type BazaDocument = { id: string; title: string; number: string | null; date: string | null; kind: string; section: string; copies: number; url: string };
export type BazaDocuments = { enabled: boolean; items: BazaDocument[]; total: number; searchUrl: string; error?: string };

export const companiesApi = {
  list: (q: { q?: string; owner?: string; sort?: CompanySort; dir?: "asc" | "desc"; page?: number; size?: number } = {}) => api<{ items: Company[]; total: number; counts: OwnerCounts }>(`/companies${qs(q)}`),
  get: (id: string) => api<Company>(`/companies/${id}`),
  create: (body: CompanyInput) => api<Company>("/companies", { method: "POST", json: body }),
  update: (id: string, body: CompanyInput) => api<Company>(`/companies/${id}`, { method: "PATCH", json: body }),
  remove: (id: string) => api(`/companies/${id}`, { method: "DELETE" }),
  attach: (id: string, contactId: string, role = "") => api(`/companies/${id}/people/${contactId}`, { method: "PUT", json: { role } }),
  detach: (id: string, contactId: string) => api(`/companies/${id}/people/${contactId}`, { method: "DELETE" }),
  conversations: (id: string) => api<{ items: import("./inbox-api").Conversation[]; total: number }>(`/companies/${id}/conversations`),
  searchMessages: (id: string, q: string) =>
    api<{ items: (import("./inbox-api").Message & { channel: string; conversationName: string })[]; total: number }>(`/companies/${id}/messages${qs({ q })}`),
  /** документы по компании из базы знаний (baza.starec.ai): инвойсы, проформы, договоры */
  bazaDocuments: (id: string) => api<BazaDocuments>(`/companies/${id}/baza-documents`),
  timeline: (id: string, kinds: string[] = []) => api<{ items: TimelineItem[] }>(`/companies/${id}/timeline${qs({ kinds: kinds.join(",") })}`),
  history: (id: string) => api<{ items: HistoryItem[] }>(`/companies/${id}/history`),
  setOwner: (id: string, userId: string | null) => api<{ ownerId: string | null; owner: { id: string; name: string } | null }>(`/companies/${id}/owner`, { method: "POST", json: { userId } }),
  duplicates: () => api<{ items: DuplicateGroup[]; total: number }>("/companies/duplicates"),
  /** слить компании ids в id: люди, сделки, заметки, задачи, клиенты Books переходят к id, дубли удаляются */
  merge: (id: string, ids: string[]) => api<MergeResult>(`/companies/${id}/merge`, { method: "POST", json: { ids } }),
};

export const zohoApi = {
  status: () => api<ZohoStatus>("/zoho/status"),
  sync: () => api("/zoho/sync", { method: "POST" }),
  wazzupImport: (full = false) => api("/zoho/wazzup/import", { method: "POST", json: { full } }),
  wazzupLink: () => api("/zoho/wazzup/link", { method: "POST" }),
  dedupeContacts: () => api("/zoho/contacts/dedupe", { method: "POST" }),
  salespersons: () => api<SalespersonsStatus>("/zoho/salespersons"),
  salespersonsRun: (scope: "archive" | "all" = "archive") => api("/zoho/salespersons/run", { method: "POST", json: { scope } }),
  salespersonLink: (name: string, body: { userId?: string | null; createUser?: { email: string; role: string }; scope?: "archive" | "all" }) =>
    api<SalespersonRow & { assigned: number }>(`/zoho/salespersons/${encodeURIComponent(name)}`, { method: "PATCH", json: body }),
  customers: (q: { status?: string; q?: string; page?: number; size?: number } = {}) => api<{ items: ZohoCustomer[]; total: number }>(`/zoho/customers${qs(q)}`),
  link: (booksId: string, body: { companyId?: string; contactId?: string }) => api<ZohoCustomer>(`/zoho/customers/${booksId}/link`, { method: "POST", json: body }),
  reject: (booksId: string) => api<ZohoCustomer>(`/zoho/customers/${booksId}/reject`, { method: "POST" }),
  reset: (booksId: string) => api<ZohoCustomer>(`/zoho/customers/${booksId}/reset`, { method: "POST" }),
  ofContact: (contactId: string) => api<{ company: { id: string; name: string; role: string } | null; customers: ZohoCustomer[] }>(`/zoho/contacts/${contactId}`),
};

export const aed = (n: number) => `AED ${Math.round(n).toLocaleString(locale)}`;

/** открыть карточку компании из другого раздела (контакт, сопоставление) */
let pendingCompany: string | null = null;
export const showCompany = (id: string) => {
  pendingCompany = id;
};
export const takeCompany = () => {
  const id = pendingCompany;
  pendingCompany = null;
  return id;
};
